"use client";

import React from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

export default function MissionVisionValues() {
  const { isDark } = useTheme();

  const sections = [
    {
      title: "Our Mission",
      // ✅ SEO: naturally includes target keywords — software, startups, enterprises
      description:
        "At Fiieve, we create reliable, innovative software solutions — from web development and mobile apps to AI automation — that help startups and enterprises grow and stay ahead in a fast-paced digital world.",
    },
    {
      title: "Our Vision",
      // ✅ SEO: positions Fiieve as a global tech leader — authority signal
      description:
        "To be a trusted global technology partner delivering cutting-edge software, engineering, and AI-driven solutions that drive business progress and shape the connected digital world.",
    },
    {
      title: "Our Values",
      // ✅ SEO: includes trust language matching SEO doc — transparency, quality, results
      description:
        "We operate with integrity, innovation, and collaboration — prioritising code quality, transparent communication, and long-term client success in every project we deliver.",
    },
  ];

  const cardFrom = isDark ? "from-[#0d0d0d]" : "from-[#1a2e1a]";
  const cardTo   = isDark ? "to-[#1a1a1a]"   : "to-[#0f1f0f]";
  const greenText = isDark ? "text-green-400" : "text-green-700";

  return (
    // ✅ SEO: section with aria-labelledby for screen readers & Google crawlers
    // ✅ Spacing: removed -mt-16, added pt-10 pb-12 so it breathes away from StatisticsSection
    <section
      aria-labelledby="mission-vision-heading"
      className={`relative overflow-hidden pt-6 pb-8 md:pt-8 md:pb-10 flex flex-col items-center justify-center px-6 transition-colors duration-300 ${
        isDark ? "bg-black text-white" : "bg-transparent text-gray-900"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
    >
      {/* Light mode atmospheric glows */}
      {!isDark && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{
            position: "absolute", top: "-10%", left: "-8%",
            width: "45vw", height: "45vw",
            background: "radial-gradient(circle, rgba(74,222,128,0.10) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
          <div style={{
            position: "absolute", top: "-10%", right: "-8%",
            width: "40vw", height: "40vw",
            background: "radial-gradient(circle, rgba(251,146,60,0.08) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
        </div>
      )}

      {/* ✅ SEO: section label above cards — gives Google context for this content block */}
      <motion.div
        className="relative text-center mb-8"
        style={{ zIndex: 1 }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <p className={`text-sm font-semibold uppercase tracking-widest ${greenText} mb-1`}>
          What Drives Us
        </p>
        {/* ✅ SEO: H2 with id linked to aria-labelledby on section */}
        <h2
          id="mission-vision-heading"
          className={`text-2xl sm:text-3xl md:text-4xl font-bold ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          Our Mission, Vision &{" "}
          <span className={greenText}>Core Values</span>
        </h2>
      </motion.div>

      <motion.div
        className="relative w-full max-w-7xl"
        style={{ zIndex: 1 }}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section, index) => (
            <motion.article
              key={index}
              // ✅ SEO: <article> signals standalone meaningful content to crawlers
              aria-label={section.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 0.7, ease: "easeOut" }}
              viewport={{ once: true }}
              className={`bg-gradient-to-b ${cardFrom} ${cardTo} hover:-translate-y-5
                         border border-green-400/40 rounded-2xl
                         shadow-[0_0_20px_rgba(16,185,129,0.15)]
                         p-6 flex flex-col items-center text-center
                         hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]
                         hover:scale-105 transition-all duration-300 min-h-[180px]`}
            >
              {/* ✅ SEO: H3 — correct hierarchy under section H2 */}
              <h3 className="text-xl md:text-2xl font-bold text-green-400 mb-3">
                {section.title}
              </h3>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                {section.description}
              </p>
            </motion.article>
          ))}
        </div>
      </motion.div>
    </section>
  );
}